import React, { createContext, useEffect, useState, useRef } from 'react'
import { UUID } from "uuidjs";
import { replaceHungarianChars } from '../helpers/strings'
import { OkeMonitorProvider } from './OkeMonitorContext';

const OkeFrameContext = createContext()

const sunmiSerialPorts = {
  "/dev/ttyHSL1": ["T1", "T1mini", "T2", "T2lite"],
  "/dev/ttyHSL3": ["T1", "T2", "S2", "T2lite", "X2", "T2mini"],
  "/dev/ttyS1": ["T2s", "T2slite", "N1", "D2", "D1s", "T2s_LITE"],
  "/dev/ttyS2": ["FT2mini"],
  "/dev/ttyS3": ["N1", "D2", "D1s", "s2cc", "D2s_LITE_d_2nd_STGL"],
  "/dev/ttyS0": ["D2s_KDS", "D2_2nd"],
  "/dev/ttyHS1": ["D3 PRO", "P2_SMARTPAD"],
  "/dev/ttyHS2": ["T3 PRO", "T3 PRO MAX"],
  "/dev/ttyUSB0": ["V3 MIX"]
}

function OkeFrameProvider(props) {
  const [frameBrandID, setFrameBrandID] = useState(null)
  const [contextInited, setContextInited] = useState(false)
  const [hasFrameContext] = useState(true)
  const [frameType, setFrameType] = useState('')
  const [frameVersion, setFrameVersion] = useState(null)
  const [brand, setBrand] = useState('')
  const [model, setModel] = useState('')

  const [systemInfo, setSystemInfo] = useState(null)

  const [nfcTagValue, setNfcTagValue] = useState(null)
  const promises = useRef({})
  const postMessage = useRef(null)
  const frameTypeRef = useRef('')
  const brandRef = useRef('')
  const modelRef = useRef('')
  let secondScreenTimeout = useRef(null)
  let secondScreenTimeoutInvoked = useRef(false)


  const callbackMessageToMain = (event, data) => {
    const e = new CustomEvent('onMessageForMain', { detail: data })
    window.dispatchEvent(e)
  }

  const callbackMessageToSecondary = (event, data) => {
    const e = new CustomEvent('onMessageForSecondary', { detail: data })
    window.dispatchEvent(e)
  }

  const determineFrameType = () => {
    if (window.Android && typeof window.Android.postMessage === 'function') {
      postMessage.current = window.Android.postMessage
      setFrameType('ANDROID')
    } else {
      if (window.webkit?.messageHandlers?.IOS != null) {
        postMessage.current = window.webkit.messageHandlers.IOS.postMessage
        setFrameType('IOS')
      } else {
        if (window.okeElectron != null) {
          setFrameType('okeElectron')
          window.okeElectron.onMessageToSecondary(callbackMessageToSecondary)
          window.okeElectron.onMessageToMain(callbackMessageToMain)
        } else {
          if (window.okeAndroid != null) {
            setFrameType('okeAndroid')
            //window.okeElectron.onMessageToSecondary(callbackMessageToSecondary)
            //window.okeElectron.onMessageToMain(callbackMessageToMain)
          } else {
            setFrameType('NONE')
          }
        }
      }
    }
  }

  const messageTimeOut = (uid) => {
    if (promises.current[uid] != null) {
      promises.current[uid].resolve({ success: false, code: 999, message: 'Timeout', data: { success: false, code: 999, message: 'Timeout' } })
      delete promises.current[uid]
    }
  }

  const onMessageFromWebView = (payload) => {
    //console.log('onMessageFromWebView', JSON.stringify(payload.data, null, 2))
    if (payload.data.source === 'react-devtools-content-script') return null
    if (payload.data.source === 'react-devtools-bridge') return null
    try {
      if (payload.data == null || payload.data === '') return
      let data = payload.data
      try {
        data = JSON.parse(payload.data)
      } catch (e) {
        data = payload.data
      }

      if (data?.action === 'sendMessageToSecondary') {
        if (data?.data?.brandID != null) {
          setFrameBrandID(data.brandID)
        }
      }

      if (data?.action === 'nfcTagDetected') {
        if (data?.data?.tagValue != null) {
          setNfcTagValue(data?.data?.tagValue)
        }
      }

      if (data?.uid != null) {
        if (promises.current[data.uid] != null) {
          if (promises.current[data.uid].action === 'getSerialPorts') {
            if (data?.data?.success) {
              for (const port in sunmiSerialPorts) {
                if (sunmiSerialPorts[port].includes(modelRef.current)) {
                  let row = { label: port, value: "SER|" + port }
                  data.data.data.push(row)
                }
              }
            }
          }
          if (promises.current[data.uid].action === 'fiscatGetSerialDevices') {
            if (data?.data?.success) {
              for (const port in sunmiSerialPorts) {
                if (sunmiSerialPorts[port].includes(modelRef.current)) {
                  let row = { label: port, value: port }
                  data.data.data.push(row)
                }
              }
            }
          }
          promises.current[data.uid].resolve(data)
          delete promises.current[data.uid]
        }
      }
    } catch (e) {
      console.log('Error - onMessageFromWebView', e)
    }
  }



  const sendMessageToWebView = async (payload, timeout = 10000) => {
    //console.log('sendMessageToWebView', payload)
    if (frameTypeRef.current === 'okeAndroid') {
      let uid = UUID.generate()
      payload.uid = uid
      const myPromise = new Promise((resolve, reject) => {
        try {
          switch (payload.action) {
            case 'getSerialPorts': {
              promises.current[uid] = { resolve, reject, action: 'getSerialPorts' }
              window.okeAndroid.getSerialPorts(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
              break
            }
            case 'getBTDevices': {
              promises.current[uid] = { resolve, reject, action: 'getBTDevices' }
              window.okeAndroid.getBTDevices(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
              break
            }
            case 'casPRiiCommand': {
              promises.current[uid] = { resolve, reject, action: 'casPRiiCommand' }
              window.okeAndroid.casPRiiCommand(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
              break
            }
            case 'oneWaySerialCommand': {
              promises.current[uid] = { resolve, reject, action: 'oneWaySerialCommand' }
              window.okeAndroid.oneWaySerialCommand(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
              break
            }
            case 'oneWayBTCommand': {
              promises.current[uid] = { resolve, reject, action: 'oneWayBTCommand' }
              window.okeAndroid.oneWayBTCommand(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
              break
            }
            case 'startPayment': {
              promises.current[uid] = { resolve, reject, action: 'startPayment' }
              window.okeAndroid.startPayment(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
              break
            }
            case 'simpleBusinessApp': {
              promises.current[uid] = { resolve, reject, action: 'simpleBusinessApp' }
              window.okeAndroid.startPayment(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
              break
            }
            case 'queryPayment': {
              promises.current[uid] = { resolve, reject, action: 'queryPayment' }
              window.okeAndroid.queryPayment(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
              break
            }
            case 'updatePayment': {
              promises.current[uid] = { resolve, reject, action: 'updatePayment' }
              window.okeAndroid.updatePayment(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
              break
            }
            case 'abortPayment': {
              promises.current[uid] = { resolve, reject, action: 'abortPayment' }
              window.okeAndroid.abortPayment(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
              break
            }
            case 'setVivaMode': {
              promises.current[uid] = { resolve, reject, action: 'setVivaMode' }
              window.okeAndroid.setVivaMode(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
              break
            }
            case 'fiscatCommands': {
              promises.current[uid] = { resolve, reject, action: 'fiscatCommands' }
              window.okeAndroid.fiscatCommands(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
              break
            }
            case 'escPosPrint': {
              promises.current[uid] = { resolve, reject, action: 'escPosPrint' }
              for (let row of payload?.data?.printSlip?.Rows || []) {
                row.Msg = replaceHungarianChars(row.Msg)
              }
              window.okeAndroid.escPosPrint(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
              break
            }
            case 'escposGetUsbDevices': {
              promises.current[uid] = { resolve, reject, action: 'escposGetUsbDevices' }
              window.okeAndroid.escposGetUsbDevices(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
              break
            }
            case 'getAppVersion': {
              promises.current[uid] = { resolve, reject, action: 'getAppVersion' }
              window.okeAndroid.getAppVersion(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
              break
            }
            case 'restartApp': {
              window.okeAndroid.restartApp()
              break
            }
            case 'exitApp': {
              window.okeAndroid.exitApp()
              break
            }
            // NEW 2023.03.17
            case 'appLoaded': {
              window.okeAndroid.appLoaded(JSON.stringify(payload))
              break
            }
            case 'setAppUrl': {
              window.okeAndroid.setAppUrl(JSON.stringify(payload))
              break
            }
            case 'switchAppMode': {
              window.okeAndroid.switchAppMode(JSON.stringify(payload))
              break
            }
            // END NEW 2023.03.17
            case 'sendMessageToMain': {
              window.okeAndroid.sendMessageToMain(JSON.stringify(payload))
              break
            }
            case 'sendMessageToSecondary': {
              window.okeAndroid.sendMessageToSecondary(JSON.stringify(payload))
              break
            }
            case 'showSecondaryWindow': {
              window.okeAndroid.showSecondaryWindow()
              break
            }
            case 'hideSecondaryWindow': {
              window.okeAndroid.hideSecondaryWindow()
              break
            }
            case 'fiscatGetAEEConfig': {
              promises.current[uid] = { resolve, reject, action: 'fiscatGetAEEConfig' }
              window.okeAndroid.fiscatGetAEEConfig(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
              break
            }
            case 'fiscatSetAEEConfig': {
              promises.current[uid] = { resolve, reject, action: 'fiscatSetAEEConfig' }
              window.okeAndroid.fiscatSetAEEConfig(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
              break
            }
            case 'fiscatDeleteAEEConfig': {
              promises.current[uid] = { resolve, reject, action: 'fiscatDeleteAEEConfig' }
              window.okeAndroid.fiscatDeleteAEEConfig(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
              break
            }
            case 'fiscatGetPairedDevice': {
              promises.current[uid] = { resolve, reject, action: 'fiscatGetPairedDevice' }
              window.okeAndroid.fiscatGetPairedDevice(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
              break
            }
            case 'fiscatGetUsbDevices': {
              promises.current[uid] = { resolve, reject, action: 'fiscatGetUsbDevices' }
              window.okeAndroid.fiscatGetUsbDevices(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
              break
            }
            case 'fiscatGetSerialDevices': {
              promises.current[uid] = { resolve, reject, action: 'fiscatGetSerialDevices' }
              window.okeAndroid.fiscatGetSerialDevices(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
              break
            }
            case 'fiscatStartService': {
              promises.current[uid] = { resolve, reject, action: 'fiscatStartService' }
              window.okeAndroid.fiscatStartService(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
              break
            }
            case 'fiscatStopService': {
              promises.current[uid] = { resolve, reject, action: 'fiscatStopService' }
              window.okeAndroid.fiscatStopService(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
              break
            }
            case 'getScreenInfo': {
              promises.current[uid] = { resolve, reject, action: 'getScreenInfo' }
              window.okeAndroid.getScreenInfo(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
              break
            }
            case 'fetchkePost': {
              let headers = []
              for (let header of Object.entries(payload.data?.headers || {})) {
                headers.push({ key: header[0], value: header[1] })
              }
              payload.data.headers = headers
              payload.data.body = JSON.stringify(payload.data.body)
              promises.current[uid] = { resolve, reject, action: 'fetchkePost' }
              window.okeAndroid.fetchkePost(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
              break
            }
            case 'fetchkeGet': {
              let headers = []
              for (let header of Object.entries(payload.data?.headers || {})) {
                headers.push({ key: header[0], value: header[1] })
              }
              payload.data.headers = headers
              promises.current[uid] = { resolve, reject, action: 'fetchkeGet' }
              window.okeAndroid.fetchkeGet(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
              break
            }

            case 'setScreenOrientation': {
              promises.current[uid] = { resolve, reject, action: 'setScreenOrientation' }
              window.okeAndroid.setScreenOrientation(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
              break
            }
            case 'sunmiShowDigital': {
              window.okeAndroid.sunmiShowDigital(JSON.stringify(payload))
              break
            }
            default: {
              resolve({ success: false, code: 400, message: 'Command not implemented', data: { success: false } })
            }
          }
        } catch (e) {
          resolve({ success: false, data: { success: false } })
          console.log(e)
        }
      })
      return myPromise
    }
    if (frameTypeRef.current === 'okeElectron') {
      try {
        switch (payload.action) {
          case 'oneWaySerialCommand': {
            let res = await okeElectron.simpleSerialCommand(payload.data)
            return { data: res }
          }
          case 'casPRiiCommand': {
            let res = await okeElectron.casPRiiCommand(payload.data)
            return { data: res }
          }
          case 'startPayment': {
            let res = await okeElectron.startPayment(payload.data)
            return { data: res }
          }
          case 'queryPayment': {
            let res = await okeElectron.queryPayment(payload.data)
            return { data: res }
          }
          case 'updatePayment': {
            let res = await okeElectron.updatePayment(payload.data)
            return { data: res }
          }
          case 'fiscatCommands': {
            let res = await okeElectron.fiscatCommands(payload.data)
            return res
          }
          case 'monitorStart': {
            let res = await okeElectron.monitorStart(payload.data)
            return res
          }
          case 'monitorState': {
            let res = await okeElectron.monitorState(payload.data)
            return res
          }
          case 'escPosPrint': {
            let res = await okeElectron.escPosPrint(payload.data)
            return res
          }
          case 'sendMessageToMain': {
            let res = await okeElectron.sendMessageToMain(payload.data)
            return res
          }
          case 'sendMessageToSecondary': {
            let res = await okeElectron.sendMessageToSecondary(payload.data)
            return res
          }
          case 'showSecondaryWindow': {
            let res = await okeElectron.showSecondaryWindow()
            return res
          }
          case 'hideSecondaryWindow': {
            let res = await okeElectron.hideSecondaryWindow()
            return res
          }
          case 'switchAppMode': {
            let res = await okeElectron.switchAppMode(payload.data)
            return res
          }
          case 'setAppUrl': {
            let res = await okeElectron.setAppUrl(payload.data)
            return res
          }
          case 'appLoaded': {
            let res = await okeElectron.appLoaded(payload.data)
            return res
          }
          case 'restartApp': {
            let res = await okeElectron.restartApp()
            return res
          }
          case 'exitApp': {
            let res = await okeElectron.exitApp()
            return res
          }
          case 'logoff': {
            let res = await okeElectron.logoff()
            return res
          }
          case 'getPrinters': {
            let res = await okeElectron.getPrinters(payload.data)
            return res
          }
          case 'getSerialPorts': {
            let res = await okeElectron.getSerialPorts(payload.data)
            return res
          }
          case 'getAppVersion': {
            let res = await okeElectron.getAppVersion(payload.data)
            return res
          }
          case 'shellExecute': {
            let res = await okeElectron.shellExecute(payload.data)
            return res
          }
          case 'setAppAsWindowsShell': {
            let res = await okeElectron.setAppAsWindowsShell(payload.data)
            return res
          }
          case 'unsetAppAsWindowsShell': {
            let res = await okeElectron.unsetAppAsWindowsShell(payload.data)
            return res
          }
          case 'isAppSetAsWindowsShell': {
            let res = await okeElectron.isAppSetAsWindowsShell(payload.data)
            return res
          }
          case 'runExplorer': {
            let res = await okeElectron.runExplorer(payload.data)
            return res
          }
          case 'bboxFunction': {
            let res = await okeElectron.bboxFunction(payload.data)
            if (res.success && payload.data?.functionName === 'Document') {
              if (res.data?.return?.Error?.AdeleErrorType === 'errOK') {
                if (res.data?.return?.DocumentID) {
                  res.data.documentID = res.data.return.DocumentID || null
                }
              } else {
                res.success = false
                res.code = 400
                res.message = res.data?.return?.Error?.AdeleErrorDescription || "Fiscal error"
                res.data = {
                  errCode: "bbox." + res.data?.return?.Error?.AdeleErrorType
                }
              }
            }
            return res
          }
          case 'fetchkePost': {
            let res = await okeElectron.fetchkePost(payload.data)
            return res
          }
          case 'fetchkeGet': {
            let res = await okeElectron.fetchkeGet(payload.data)
            return res
          }
          default: {
            return { success: false, code: 400, message: 'Command not implemented', data: { success: false } }
          }
        }
      } catch (e) {
        return { success: false, code: 500, message: 'OkeElectron frame error', data: { success: false } }
      }
    } else {
      let uid = UUID.generate()
      payload.uid = uid
      const myPromise = new Promise((resolve, reject) => {
        try {
          if (frameType === 'NONE') {
            resolve({ success: false, code: 999, message: 'Not in frame', data: { success: false, code: 999, message: 'Not in frame' } })
          } else {
            if (postMessage.current != null) {
              promises.current[uid] = { resolve, reject }
              postMessage.current(JSON.stringify(payload))
              setTimeout(() => messageTimeOut(uid), timeout)
            } else {
              resolve({ success: false, data: { success: false } })
            }
          }
        } catch (e) {
          console.error(e)
          resolve({ success: false, data: { success: false } })
        }
      })
      return myPromise
    }
  }

  const checkSecondScreen = async () => {
    if (frameTypeRef.current !== '' && ['okeElectron', 'okeAndroid'].includes(frameTypeRef.current)) {
      await sendMessageToWebView({ action: 'sendMessageToSecondary', data: { action: "systemLocalPing" } })
    }
    secondScreenTimeout.current = setTimeout(() => {
      checkSecondScreen()
    }, 1000)
  }

  const invokeSecondScreenCheck = () => {
    if (secondScreenTimeoutInvoked.current) return
    secondScreenTimeoutInvoked.current = true
    secondScreenTimeout.current = setTimeout(() => {
      checkSecondScreen()
    }, 1000)
  }

  useEffect(() => {
    frameTypeRef.current = frameType
    sendMessageToWebView({
      action: 'appLoaded',
      data: {
        url: window.location.hostname
      }
    })
    sendMessageToWebView({
      action: 'getAppVersion',
      data: {}
    }).then(res => {
      //console.log('getAppVersion', JSON.stringify(res))
      if (res?.data?.data != null) {
        res = res.data
      }
      if (res?.success) {
        setFrameVersion(res.data.version)
        setBrand(res.data.brand)
        brandRef.current = res.data.brand
        setModel(res.data.model)
        modelRef.current = res.data.model
        setSystemInfo(res.data)
      } else {
        setFrameVersion('OLD')
      }
    }).catch(e => {
      console.log(e)
    })
  }, [frameType])

  useEffect(() => {
    document.addEventListener('message', onMessageFromWebView)
    window.addEventListener('message', onMessageFromWebView)
    determineFrameType()
    setContextInited(true)
    return () => {
      // ON UNMOUNT
      document.removeEventListener('message', onMessageFromWebView)
      window.removeEventListener('message', onMessageFromWebView)
    }
  }, [])

  return (
    <OkeFrameContext.Provider
      value={{
        hasFrameContext, contextInited, sendMessageToWebView,
        frameType, frameVersion, frameBrandID, invokeSecondScreenCheck,
        brand, model, systemInfo,
        nfcTagValue, setNfcTagValue
      }}
    >
      <OkeMonitorProvider sendMessageToWebView={sendMessageToWebView} frameType={frameType} frameVersion={frameVersion}>
        {props.children}
      </OkeMonitorProvider>
    </OkeFrameContext.Provider>
  )
}

export { OkeFrameContext, OkeFrameProvider }
